/* System defined core library */
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as $ from 'jquery';
import { Observable, Observer, Subscription, forkJoin } from 'rxjs';
import * as moment from 'moment';

/* User defined library/components */
import { MapUtilityService } from '../../../../map/services/map-utility.service';
import { TimerService } from '../../../../shared/services/timer.service';
import { UtilityService } from '../../../../shared/services/utility.service';
import { SchedulingApiService } from '../../services/scheduling-api.service';
import { SelectedExamDetailService } from '../../services/selected-exam-detail.service';
import { SitesService } from '../../services/sites/sites.service';
import { BootstrapParams } from '../../../../../bootstrap-params';

/* User defined models */
import { AppointmentDetail } from '../../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../../shared/models/scheduling/appointmentSlot.model';
import { Client } from '../../../../shared/models/scheduling/client.model';
import { Country } from '../../../../shared/models/scheduling/country.model';
import { EligibilityDetail } from '../../../../shared/models/scheduling/eligibility.model';
import { Exam } from '../../../../shared/models/scheduling/exam.model';
import { Hold } from '../../../../shared/models/scheduling/hold.model';
import { Program } from '../../../../shared/models/scheduling/program.model';
import { Session } from '../../../../shared/models/scheduling/session.model';
import { SessionGroup } from '../../../../shared/models/scheduling/sessionGroup.model';
import { SiteSlot } from '../../../../shared/models/scheduling/siteSlot.model';
import { Slot } from '../../../../shared/models/scheduling/slot.model';
import { State } from '../../../../shared/models/scheduling/state.model';
import { TestingAccommodation } from '../../../../shared/models/scheduling/testingAccommodation.model';

/* User defined enums/constants */
import * as constants from '../../../../shared/constants/scheduling.constant';
import { ComponentType, DistanceUnits } from '../../../../shared/enums/scheduling.enum';


@Component({
  selector: 'app-multiday-testcenter-selector',
  templateUrl: './multiday-testcenter-selector.component.html',
  styleUrls: ['./multiday-testcenter-selector.component.scss']
})
export class MultidayTestCenterSelectorComponent implements OnInit {

  /* User defined private/public variables  */
  public client: Client;
  public program: Program;
  public exam: Exam;
  public selectedStartDate: any;
  public selectedEndDate: any;
  public region: any;
  public country: Country;
  public state: State;
  public popupDateSelection: any;
  public popLocationSelector: any;
  public popDateRangeSelector: any;
  public locationSelector: any;
  public sites: SiteSlot[];
  public applySortBy: string;
  public selectedAppointments: AppointmentDetail[] = []
  public takeSiteSlotsize: number;
  public skipSiteSlotsize: number;
  public applicableTestingAccommodations: TestingAccommodation[];
  public isMapsValid: boolean = true;
  public examWithTA: Exam[] = [];
  public eligibilityDetails: EligibilityDetail[] = [];
  public selectedSite: SiteSlot;
  public areMoreSitesAvailable: boolean = true;
  public holds: Hold[];
  public sessions: Session[] = [];
  public maxSessionLength: number = 0;
  public sessionGroups: SessionGroup[] = [];
  public sessionAvailabilities: any[];
  public selectedSlots: Slot[] = [];
  public noSessionAvailability: boolean;
  public zoom: number;
  public zoomDefault: number = 12;
  public zoomOnFocus: number = 15;
  public markers: any = [];
  public lat: number = constants.DefaultLatitude;
  public lng: number = constants.DefaultLognitude;
  public units = DistanceUnits;
  public distanceUnit: DistanceUnits;
  public placeHolderText: string;
  public today = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate()
  };
  public defaultCalendarMonth: string;
  public maxEndDateLimit: string;
  public overLapEndDate: any;
  public overLapStartDate: any;
  public myDateRangePickerOptions: any;
  public formattedAddress: string;
  public searchRegion: State;
  public selectedLocation: string;


  /* Input-OutPut parameters decalartions */
  @Output() canNavigateNext: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() chosenSessions: EventEmitter<Session[]> = new EventEmitter<Session[]>();
  @Output() setPlaceHolder = new EventEmitter<any>();
  @Input() consecutiveDays: boolean;

  /* Service subscriptions */
  client$: Subscription;
  region$: Subscription;
  program$: Subscription;
  country$: Subscription;
  state$: Subscription;
  exam$: Subscription;
  eligibility$: Subscription;
  locationSelector$: Subscription;
  sites$: Subscription;
  defaultCalendarMonth$: Subscription;
  selectedStartDate$: Subscription;
  selectedEndDate$: Subscription;
  overLapStartDate$: Subscription;
  overLapEndDate$: Subscription;
  maxEndDateLimit$: Subscription;
  holds$: Subscription;
  selectedAppointments$: Subscription;
  skipSize$: Subscription;
  eligibilityDetails$: Subscription;
  testingAccommodation$: Subscription;
  translateLanguage$: Subscription;
  distanceUnit$: Subscription;
  selectedSite$: Subscription;
  sessions$: Subscription;
  sessionGroups$: Subscription;
  sessionAvailabilities$: Subscription;

  @ViewChild('testcenterLocation') testcenterLocation: ElementRef;

  constructor(
    private mapUtilityService: MapUtilityService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private sitesService: SitesService,
    private timerService: TimerService,
    private translate: TranslateService,
    private utilityService: UtilityService,
    private bootstrapParams: BootstrapParams
  ) {
    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.client$ = selectedExamDetailService.client.subscribe(
      client => {
        this.client = client;
      });

    this.country$ = selectedExamDetailService.lpiCountry.subscribe(
      country => {
        this.country = country;
    });

    this.state$ = selectedExamDetailService.lpiState.subscribe(
      state => {
        this.state = state;
    });

    this.region$ = selectedExamDetailService.region.subscribe(region => {
      this.region = region;
    });

    this.program$ = selectedExamDetailService.program.subscribe(program => {
      this.program = program;
    });

    this.defaultCalendarMonth$ = selectedExamDetailService.defaultCalendarMonth.subscribe(defaultCalendarMonth => {
      this.defaultCalendarMonth = defaultCalendarMonth;
    });

    this.selectedStartDate$ = selectedExamDetailService.selectedStartDate.subscribe(selectedStartDate => {
      this.selectedStartDate = selectedStartDate;
    });

    this.selectedEndDate$ = selectedExamDetailService.selectedEndDate.subscribe(selectedEndDate => {
      this.selectedEndDate = selectedEndDate;
    });

    this.overLapStartDate$ = selectedExamDetailService.overlapsFromDate.subscribe(
      startDate => {
        this.overLapStartDate = startDate;
      });

    this.overLapEndDate$ = selectedExamDetailService.overlapsToDate.subscribe(
      endDate => {
        this.overLapEndDate = endDate;
      });

    this.locationSelector$ = selectedExamDetailService.locationSelector.subscribe(
      locationSelector => {
        this.locationSelector = locationSelector;
      });

    this.maxEndDateLimit$ = selectedExamDetailService.maxEndDateLimit.subscribe(
      endDate => {
        this.maxEndDateLimit = endDate;
      });

    this.exam$ = selectedExamDetailService.exam.subscribe(exam => {
      this.exam = exam;
    });

    this.zoom = this.zoomDefault;

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    this.testingAccommodation$ = selectedExamDetailService.applicableTestingAccommodations.subscribe(
      testingAccommodation => {
        this.applicableTestingAccommodations = testingAccommodation;
      });


    this.skipSize$ = selectedExamDetailService.skipSize.subscribe(
      skipSize => {
        this.skipSiteSlotsize = skipSize || 0;
      });

    this.eligibilityDetails$ = selectedExamDetailService.eligibilityDetails.subscribe(value => {
      this.eligibilityDetails = value || [];
    });

    this.sites$ = selectedExamDetailService.prevSiteSlots.subscribe(sites => {
      this.sites = sites;
    });

    this.sessions$ = selectedExamDetailService.sessions.subscribe(sessions => {
      if (sessions && sessions.length) {
        this.sessions = sessions;
      }
    });

    this.sessionGroups$ = selectedExamDetailService.sessionGroups.subscribe(sessionGroups => {
      if (sessionGroups && sessionGroups.length) {
        this.sessionGroups = sessionGroups;
      }
    });

    this.sessionAvailabilities$ = selectedExamDetailService.sessionAvailabilities.subscribe(sessionAvailabilities => {
      if (sessionAvailabilities && sessionAvailabilities.length) {
        this.sessionAvailabilities = sessionAvailabilities;
      }
    });

    this.selectedSite$ = selectedExamDetailService.siteSlot.subscribe(siteSlot => {
      this.selectedSite = siteSlot;
    });

    this.holds$ = selectedExamDetailService.holds.subscribe(holds => {
      this.holds = holds;
    });

    this.placeHolderText = this.selectedExamDetailService.dateFormat.toUpperCase();
    this.searchRegion = this.region;
    this.formattedAddress = !!this.region ? this.region.name : 'locationSelectorComponent.placeholderText';
    this.setPlaceHolder.emit(this.formattedAddress);

    this.popLocationSelector = {
      title: "",
      content: "<em class='fa fa-calendar' aria-hidden='true' style='color:green;'></em>",
      placement: "bottom",
      actionTxt: ((this.selectedAppointments) && this.selectedAppointments[0].region) ? this.selectedAppointments[0].region.name : "",
      appliedClass: "btn secNavAction",
      showHeaderFooter: false,
      refBlock: "LocationSelector",
      locationSelector: true,
      focusChildElement: 'locationSelector'
    };

    this.popupDateSelection = {
      title: "",
      content: "long page string .....................",
      placement: "bottom",
      actionTxt: "testCenterSelecterComponent.moreDatesAtThisTestCenter",
      appliedClass: "btn moreAction",
      showHeaderFooter: false,
      refBlock: "SessionDate",
      locationSelector: false,
      isSlotSelected: false,
      focusChildElement: 'calendarWrpr'
    };

    this.popDateRangeSelector = {
      title: "",
      content: "long page string .....................",
      placement: "bottom",
      actionTxt: "testCenterSelecterComponent.moreDatesAtThisTestCenter",
      appliedClass: "btn moreAction",
      showHeaderFooter: false,
      refBlock: "DateRangePicker",
      locationSelector: false,
      isSlotSelected: false,
      focusChildElement: 'calendarWrpr'
    };

    this.isMapsValid = this.selectedExamDetailService.isGoogleMapsValid;

    this.distanceUnit$ = selectedExamDetailService.distanceUnit.subscribe(unit => {
      this.distanceUnit = unit;
    });

    this.takeSiteSlotsize = this.bootstrapParams.testCenterTakeSize;
  }

  ngOnInit() {
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.MultidayTestCenterSelectorComponent)) return;
    this.exam = this.selectedAppointments[0].exam;
    this.maxSessionLength = this.exam.sessions.length;

    this.validateNavigation();

    //Set the initial size to get site Slots.
    //Default Sorting
    this.applySortBy = "Distance";

    this.examWithTA = this.selectedAppointments.map((appointment) => {
      return Exam.convertAPIObjectToExam(appointment.exam, appointment.exam.sessions, appointment.status, appointment.exam.testingAccommodations);
    });

    if (!this.sites || (this.sites && !this.sites.length)) {
      const startDate = this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedStartDate['jsdate']), "-");
      const endDate = this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedEndDate['jsdate']), "-");
      this.getSites(startDate, endDate);
    }

    if (this.selectedSite) {
      this.loadMapWithMarkers([this.selectedSite]);
    }
  }

  getValidStart() {
    return this.utilityService.getValidStart(this.overLapStartDate);
  }

  getValidStartDateEnd() {
    return this.utilityService.getValidStartDateEnd(this.overLapStartDate, this.overLapEndDate);
  }

  /* Load markers based on available sites */
  loadMapWithMarkers(sites: SiteSlot[]): void {
    this.markers = this.mapUtilityService.getMapMarkersFromSlots(sites);
  }

  sort(event: any): void {
    event.preventDefault();
    this.applySortBy = event.target.value;
    this.sites.sort(this.sitesService.ApplySorting(this.applySortBy));
    this.clearSessions();
    if (this.lat && this.region) this.loadMapWithMarkers(this.sites);
  }

  /* API call to get Sites */
  getSites(startDate: any, endDate: any): void {
    const latitude = this.region ? this.region.lat : this.country.id;
    const longitude = this.region ? this.region.long : this.state ? this.state.id : null;
    this.areMoreSitesAvailable = true;
    const eligibilityId = this.eligibilityDetails ? this.eligibilityDetails[0] && this.eligibilityDetails[0].id : '-1';
    this.schedulingApiService.getSites(
      startDate,
      endDate,
      latitude,
      longitude,
      this.takeSiteSlotsize,
      this.skipSiteSlotsize,
      false,
      this.examWithTA,
      eligibilityId,
      this.isMapsValid
    ).subscribe(response => {
      let sites = response.siteSlots;
      // Show More Button should be disabled if we run out of sites.
      this.areMoreSitesAvailable = sites.length === this.takeSiteSlotsize;
      if (sites.length == 0) {
        if ((!!this.sites && !this.sites.length) || !this.sites) {
          this.sites = [];
          this.selectedExamDetailService.setSiteSlots(this.sites);
          if (this.lat && this.region) this.loadMapWithMarkers(this.sites);
        }
      } else {
        if (this.skipSiteSlotsize > 0 && this.sites && this.sites.length > 0) {
          this.sites = this.sites.concat(sites);
        } else {
          this.sites = sites;
        }

        this.sites.sort(this.sitesService.ApplySorting(this.applySortBy));
        this.selectedExamDetailService.setSiteSlots(this.sites);
        this.clearSessions();
        if (this.lat && this.region) this.loadMapWithMarkers(this.sites);
        this.addSitesAlert();
      }
    });
  }

  milesToKm(miles: number): number {
    return this.sitesService.milesToKm(miles);
  }

  setUnit(unit: DistanceUnits) {
    this.sitesService.setUnit(unit);
  }

  /**
   * UX changes on site selection
   * Hide other map markers and sites
   * @param event
   * @param selectedStartDate
   * @param site
   */
  selectSite(event: any, selectedStartDate: any, site: SiteSlot): void {
    $('.card-heading.card-heading-sm.test-center-month div').removeClass('active');
    $('.card-heading card-heading-sm.test-center-month').removeClass('active');
    if (event && event.target) {
      $(event.target).addClass('active');
    }

    // set site
    site.Testdate = selectedStartDate;
    this.selectedSite = site;
    this.selectedExamDetailService.setSiteSlot(this.selectedSite);

    this.selectedAppointments[0].appointmentSlot = new AppointmentSlot();
    this.selectedAppointments[0].hold = new Hold();

    // Map changes
    this.lat = site.location.coordinates[0]; //focus in the site pin on map
    this.lng = site.location.coordinates[1];
    this.zoom = this.zoomOnFocus;
    this.loadMapWithMarkers([this.selectedSite]);

    // set session dates
    this.setMaxEndDate(selectedStartDate);
    this.getSessionGroups(this.selectedSite.Testdate, this.maxEndDateLimit).subscribe(() => { });
  }

  // set first session
  setFirstSession(site: SiteSlot, selectedDate: string, startDate: string, endDate: string, index: number) {
    if (index === 0) {
      const date = moment(selectedDate).format('YYYY-MM-DDTHH:mm:ss');
      const firstSession = new Session(
        this.sessions[0].startDate,
        this.sessions[0].endDate,
        site,
        this.getFirstSessionAvailability(),
        this.exam.sessions[0].selected,
        this.exam.sessions[0].duration,
        this.exam.sessions[0].confirmationNumber,
        date,
        1,
        1,
        this.utilityService.getTimeString(date, this.selectedExamDetailService.timeFormat),
        this.utilityService.getTimeString(this.getEndDateTime(date, this.exam.sessions[0].duration), this.selectedExamDetailService.timeFormat)
      );
      this.sessions[0] = firstSession;
      this.setMaxEndDate(selectedDate);

      this.exam.sessions[0].site = site;
      this.exam.sessions[0].slot = new Slot(null, null, new Date(selectedDate));
      this.exam.sessions[0].slot.testTime =
        this.utilityService.getTimeString(
          moment(this.exam.sessions[0].slot.startDateTime).format("YYYY-MM-DDTHH:mm:ss"), this.selectedExamDetailService.timeFormat
        );
      this.exam.sessions[0].slot.testEndTime =
        this.utilityService.getStartEndFormat(
          UtilityService.addMins(
            this.exam.sessions[0].slot.startDateTime,
            this.sessions[0].duration
          )
        );
      this.chosenSessions.emit(this.exam.sessions);

      this.sessions = this.sessions.slice(0, 1);

      let session = new Session(
        moment(this.sessions[0].selectedDate).add(1, 'days').format('YYYY-MM-DD'),
        this.maxEndDateLimit,
        site,
        this.getSessionAvailabilities(),
        this.exam.sessions[1].selected,
        this.exam.sessions[1].duration,
        this.exam.sessions[1].confirmationNumber
      );
      this.sessions.push(session);


      if (this.consecutiveDays) {
        this.setSessionData(null, session.availabilities[0].date, 1, session.availabilities[0].groupId);
      }

      this.myDateRangePickerOptions = {
        // other options...
        dateFormat: this.selectedExamDetailService.dateFormat.toLowerCase(),
        sunHighlight: false,
        disableUntil: this.getValidStart(),
        disableSince: this.getValidStartDateEnd(),
        firstDayOfWeek: 'su',
        disableDates: [this.today],
        showTodayBtn: false,
        placeholderText: 'MM/DD/YYYY - MM/DD/YYYY'
      }
    }
  }

  // when a date is selected
  setSessionData($event: any, selectedDate: string, index: number, groupId: number) {
    this.clearHolds();
    if (index === 0) {
      this.selectedSite.Testdate = selectedDate;
      this.setMaxEndDate(selectedDate);
      this.getSessionGroups(this.selectedSite.Testdate, this.maxEndDateLimit, this.selectedSite).subscribe(() => {
        this.setFirstSession(this.selectedSite, selectedDate, this.overLapStartDate, this.overLapEndDate, 0);
        this.setSelectedSessionData(selectedDate, index, groupId);
      });
    } else {
      this.setSelectedSessionData(selectedDate, index, groupId);
    }
  }

  setSelectedSessionData(selectedDate: string, index: number, groupId: number) {
    const selectedSession = new Session(
      this.sessions[index].startDate,
      this.sessions[index].endDate,
      this.selectedSite,
      this.sessions[index].availabilities,
      this.exam.sessions[index].selected,
      this.exam.sessions[index].duration,
      this.exam.sessions[index].confirmationNumber,
      selectedDate,
      index + 1,
      groupId,
      this.utilityService.getTimeString(selectedDate, this.selectedExamDetailService.timeFormat),
      this.utilityService.getTimeString(this.getEndDateTime(selectedDate, this.exam.sessions[index].duration), this.selectedExamDetailService.timeFormat)
    );

    this.sessions[index] = selectedSession;

    this.exam.sessions[index].site = this.selectedSite;
    this.exam.sessions[index].slot = new Slot(null, null, new Date(selectedDate));
    this.exam.sessions[index].slot.testTime =
      this.utilityService.getTimeString(
        moment(this.exam.sessions[index].slot.startDateTime).format("YYYY-MM-DDTHH:mm:ss"), this.selectedExamDetailService.timeFormat
      );
    this.exam.sessions[index].slot.testEndTime =
      this.utilityService.getStartEndFormat(
        UtilityService.addMins(
          this.exam.sessions[index].slot.startDateTime,
          this.sessions[index].duration
        )
      );
    this.chosenSessions.emit(this.exam.sessions);

    let sessionLength = this.sessions.length;
    if (this.sessions.length > (index + 1)) {
      this.clearSelection(null, index + 1);
      this.sessions = this.sessions.slice(0, index + 1);
      let session = new Session(
        moment(selectedDate).add(1, 'days').format('YYYY-MM-DD'),
        this.maxEndDateLimit,
        this.selectedSite,
        this.getSessionAvailabilities(),
        true,
        this.exam.sessions[index].duration,
        this.exam.sessions[index].confirmationNumber
      );

      if (this.consecutiveDays) {
        session.selectedDate = session.availabilities[0].date;
        session.groupId = session.availabilities[0].groupId;
        session.startDateTime = this.utilityService.getTimeString(session.selectedDate, this.selectedExamDetailService.timeFormat);
        session.endDateTime = this.utilityService.getTimeString(this.getEndDateTime(session.selectedDate, this.exam.sessions[index].duration), this.selectedExamDetailService.timeFormat);
      }
      this.sessions.push(session);
    } else if (this.sessions.length < this.maxSessionLength) {
      let session = new Session(
        moment(this.sessions[sessionLength - 1].selectedDate).add(1, 'days').format('YYYY-MM-DD'),
        this.maxEndDateLimit,
        this.selectedSite,
        this.getSessionAvailabilities(),
        true
      );

      if (this.consecutiveDays) {
        session.selectedDate = session.availabilities[0].date;
        session.groupId = session.availabilities[0].groupId;
        session.duration = this.exam.sessions[index + 1].duration;
        session.confirmationNumber = this.exam.sessions[index + 1].confirmationNumber;
        session.startDateTime = this.utilityService.getTimeString(session.selectedDate, this.selectedExamDetailService.timeFormat);
        session.endDateTime = this.utilityService.getTimeString(this.getEndDateTime(session.selectedDate, this.exam.sessions[index].duration), this.selectedExamDetailService.timeFormat);
      }
      session.site = this.selectedSite;
      this.sessions.push(session);
    }

    if (this.consecutiveDays) {

      if (this.sessions.length <= this.exam.sessions.length) {
        this.clearSelection(null, 1);
        this.sessions = this.sessions.slice(0, 1);
        const sessionLength = this.sessions.length;

        this.exam.sessions.forEach((examSession: Session, index: number) => {
          // first session already exists
          if (index > 0) {
            const selectedDate = this.sessions[sessionLength - 1].selectedDate;
            const availabilities = this.getSessionAvailabilities();
            let session = new Session(
              moment(selectedDate).add(1, 'days').format('YYYY-MM-DD'),
              this.maxEndDateLimit,
              this.selectedSite,
              this.getSessionAvailabilities(),
              true,
              this.exam.sessions[index].duration,
              this.exam.sessions[index].confirmationNumber,
              availabilities[0].date.toString(),
              index + 1,
              availabilities[0].groupId,
              this.utilityService.getTimeString(availabilities[0].date, this.selectedExamDetailService.timeFormat),
              this.utilityService.getTimeString(this.getEndDateTime(availabilities[0].date.toString(), this.exam.sessions[index].duration), this.selectedExamDetailService.timeFormat),
            );

            this.sessions.push(session);
          }
        });
      }
    }

    if (this.sessions.length === this.maxSessionLength) {
      if (this.sessions.every(s => !!s.selectedDate)) {
        this.setHolds();
      } else {
        this.selectedSlots = [];
      }
    }

    this.validateNavigation();
  }

  /**
   * When date is selected from RangePicker
   * @param selectedDate
   * @param rangeType
   * @param index
   */
  setSessionRangeDate(selectedDate: any, rangeType: string) {
    if (rangeType === 'start') {
      this.sessions[0].selectedRangeStartDate = selectedDate;
      const endDateBegin = this.getValidEndDateStart(selectedDate.date);
      const endDateEnd = this.getValidEndDateEnd(endDateBegin);
      this.myDateRangePickerOptions = {
        dateFormat: this.selectedExamDetailService.dateFormat.toLowerCase(),
        sunHighlight: false,
        disableUntil: endDateBegin,
        disableSince: endDateEnd,
        firstDayOfWeek: 'su',
        disableDates: [this.today],
        showTodayBtn: false
      };
    } else {
      // rangetype is end
      this.sessions[0].selectedRangeEndDate = selectedDate;
      this.selectedSite.Testdate = null;

      this.getSessionGroups(this.sessions[0].selectedRangeStartDate.jsdate, this.sessions[0].selectedRangeEndDate.jsdate).subscribe(() => {
        this.sessions[0].selectedDate = null;
        this.sessions[0].availabilities = this.getFirstSessionAvailability();
        this.clearSelection(null, 0);
      });

      this.myDateRangePickerOptions = {
        // other options...
        dateFormat: this.selectedExamDetailService.dateFormat.toLowerCase(),
        sunHighlight: false,
        disableUntil: this.getValidStart(),
        disableSince: this.getValidStartDateEnd(),
        firstDayOfWeek: 'su',
        disableDates: [this.today],
        showTodayBtn: false,
        placeholderText: 'MM/DD/YYYY - MM/DD/YYYY'
      }
    }
  }

  /**
   * Get first session availabilities
   * */
  getFirstSessionAvailability() {
    let result = this.sessionGroups.map((sg) => {
      return {
        "groupId": sg.groupId,
        "date": sg.sessions[0].slot.startDateTime,
        "slotId": sg.sessions[0].slot.id
      }
    });
    result = this.getDistinctValues(result);
    return result;
  }

  /**
   * get all session availabilities
   * */
  getSessionAvailabilities() {
    let groups: any = [];
    this.sessions.forEach((session, index) => {
      let selectedDate = moment(session.selectedDate).format('YYYY-MM-DDTHH:mm:ss');
      let sessionGrp = this.sessionGroups.map((sg) => {
        let avail = sg.sessions.filter((s) => {
          return (s.sessionId === index + 1) && s.slot.startDateTime.toString() === selectedDate;
        })
        if (avail.length) return avail[0].groupId;
      });
      sessionGrp = sessionGrp.filter(sg => !!sg);
      groups.push(sessionGrp);
    })
    groups = this.getCommonElements(groups);
    let result = this.sessionGroups.map((sg) => {
      if (groups.indexOf(sg.groupId) > -1) {
        return {
          "groupId": sg.groupId,
          "date": sg.sessions[this.sessions.length].slot.startDateTime,
          "slotId": sg.sessions[this.sessions.length].slot.id
        }
      }
    });
    result = result.filter(r => !!r);
    result = this.getDistinctValues(result);
    return result;
  }

  /**
   * Set availabilities when a date from datepicker is chosen
   * @param $event
   * @param index
   */
  getSessionData($event: any, index: number) {
    const date = moment(new Date($event['jsdate'])).format('YYYY-MM-DD');

    this.updateSessionAvailabilities();

    let availabilities = this.sessionAvailabilities[index];
    if (availabilities) {
      const selected = availabilities.filter((a: any) => {
        return a.testDate === date;
      });
      if (selected.length) {
        this.noSessionAvailability = false;
        this.sessions[index].availabilities = [{
          "groupId": selected[0].groupId,
          "date": selected[0].slot.startDateTime,
          "slotId": selected[0].slot.id
        }]
        this.setSessionData(null, selected[0].startDateTime, index, selected[0].groupId);
      } else {
        this.noSessionAvailability = true;
      }
    }
  }
  /**
   * Set endDateLimit based on consecutive/non consectuive
   * configuration of maxDays in GEE
   * @param selectedStartDate
   */
  setMaxEndDate(selectedStartDate: any) {
    let daysToAdd = this.selectedExamDetailService.maxDays > 0 ? this.selectedExamDetailService.maxDays : 14;
    const maxEndDate = moment(selectedStartDate).add(
      daysToAdd
      , 'day').format('YYYY-MM-DD');
    const overLap = moment(this.overLapEndDate, 'MM-DD-YYYY').format('YYYY-MM-DD');

    if (moment(overLap).isBefore(maxEndDate)) {
      this.maxEndDateLimit = overLap;
      this.selectedExamDetailService.setmaxEndDateLimit(overLap);
    } else {
      this.maxEndDateLimit = maxEndDate;
      this.selectedExamDetailService.setmaxEndDateLimit(maxEndDate);
    }
  }

  getAvailabilitiesByDate(selectedStartDate: any, site: SiteSlot) {
    this.clearAll();
    this.selectedSite = site;
    this.selectedSite.Testdate = null;

    const startDate = moment(new Date(selectedStartDate['jsdate'])).format('YYYY-MM-DDTHH:mm:ss');
    this.setMaxEndDate(startDate);

    this.getSessionGroups(startDate, this.maxEndDateLimit, site).subscribe(() => {
      // first session
      this.sessions[0].availabilities = this.getFirstSessionAvailability();
    });
  }

  setHolds() {
    const selectedGroupId = this.sessions[this.sessions.length - 1].groupId;
    const selectedGroup = this.sessionGroups.filter((sg) => sg.groupId === selectedGroupId);
    if (selectedGroup.length) {
      selectedGroup.forEach((sg) => {
        this.selectedSlots = sg.sessions.map(s => s.slot);
      })
    }

    this.sessions.forEach((s, i) => {
      s.slot = this.selectedSlots[i];
      s.hold = new Hold();
      s.hold.slot = this.selectedSlots[i];
    });
    this.selectedExamDetailService.setSessions(this.sessions);
    this.chosenSessions.emit(this.sessions);
  }

  validateNavigation() {
    if (!this.holds || (this.holds && !this.holds.length)) {
      this.canNavigateNext.emit(!!this.selectedSlots.length);
    } else {
      this.canNavigateNext.emit(!!(this.holds && this.holds.length));
    }
  }

  showClearAll() {
    return this.sessions.length && this.selectedSite;
    //return this.sessions.some(s => !!s.selectedDate);
  }

  /**
   * Clear session selection based on Index
   * @param $event
   * @param index
   */
  clearSelection($event: any, index: number) {
    this.sessions.forEach((session, i) => {
      if (i >= index) {
        session.selectedDate = '';
        session.groupId = null;
        this.exam.sessions[i].slot = null;
      }
    });
    this.sessions = this.sessions.slice(0, index + 1);
    this.clearSlotSelection();
  }

  /*
   * Clear All sessions
   * */
  clearAll() {
    this.loadMapWithMarkers(this.sites);
    this.sessionAvailabilities = [];
    this.sessionGroups = [];
    this.selectedSite = null;
    this.clearSessions();
    this.clearSlotSelection();
  }

  /** Load more sites when "More Sites"is clicked */
  showMoreSites() {
    $(".show-more-sites").addClass('no-outline');
    this.skipSiteSlotsize += this.takeSiteSlotsize;
    this.selectedExamDetailService.setSkipSize(this.skipSiteSlotsize);
    const startDate = this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedStartDate['jsdate']), "-");
    const endDate = this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedEndDate['jsdate']), "-");
    this.getSites(startDate, endDate);
  }

  //Adding a a one time per click alert to be read by a screenreader when more sites are retrieved
  addSitesAlert() {
    if (this.areMoreSitesAvailable) {
      const sitesAlert: HTMLElement = document.getElementById('moreSitesAlert');
      var div = document.createElement('div');
      var alertText = document.createTextNode("More Sites have been added to the list");
      div.appendChild(alertText);
      div.setAttribute('role', 'alert');
      sitesAlert.append(div);

      setTimeout(() => {
        sitesAlert.removeChild(div);
      }, 5000);
    }
  }

  /**
   * Get Session availabilities
   * */
  getSessionGroups(startDate: any, endDate: any, site?: SiteSlot) {
    //this.selectedExamDetailService.setSessions([]);
    return new Observable((observer: Observer<any>) => {
      this.getSessions(startDate, endDate, site).subscribe((sessionGroups: SessionGroup[]) => {
        this.sessionAvailabilities = [];
        this.exam.sessions.forEach((s, index) => {
          let availability = sessionGroups.map(sg => {
            s.duration = sg.sessions.filter(group => group.sessionId === index + 1)[0].slot.duration;
            return {
              "sessionId": index + 1,
              "groupId": sg.sessions.filter(group => group.sessionId === index + 1)[0].groupId,
              "slot": sg.sessions.filter(group => group.sessionId === index + 1)[0].slot,
              "testDate": sg.sessions.filter(group => group.sessionId === index + 1)[0].slot.testDate,
              "startDateTime": sg.sessions.filter(group => group.sessionId === index + 1)[0].slot.startDateTime,
              "duration": sg.sessions.filter(group => group.sessionId === index + 1)[0].slot.duration
            };
          });

          availability = availability.reduce((a: any, c: any) => {
            const x = a.find((item: any) => item.testDate === c.testDate);
            if (!x) {
              return a.concat([c]);
            } else {
              return a;
            }
          }, []);
          this.sessionAvailabilities.push(availability);
        });
        if (!!this.sessionAvailabilities && this.sessionAvailabilities.every(a => a.length) && !!this.selectedSite && this.selectedSite.Testdate) {
          this.sessionAvailabilities.forEach((sa) => {
            const selected = sa.filter((avail: any) => {
              return avail.sessionId === 1 && avail.testDate === moment(this.selectedSite.Testdate).format('YYYY-MM-DD')
            });

            if (selected.length) {
              this.selectedSite.Testdate = selected[0].startDateTime;
            }
          });
          this.setFirstSession(this.selectedSite, this.selectedSite.Testdate, this.overLapStartDate, this.overLapEndDate, 0);
        }
        this.selectedExamDetailService.setSessionAvailabilities(this.sessionAvailabilities);
        observer.next(null);
        observer.complete();
      });
    });
  }

  getSessions(startDate: any, endDate: any, site: SiteSlot) {
    this.sessionGroups = [];
    let eligibilityId = this.eligibilityDetails ? this.eligibilityDetails[0] && this.eligibilityDetails[0].id : '-1';
    const selectedSiteId = site ? site.id : this.selectedSite.id
    startDate = moment(startDate).format('YYYY-MM-DDTHH:mm:ss');
    endDate = moment(endDate).format('YYYY-MM-DDTHH:mm:ss');
    return new Observable((observer: Observer<any>) => {
      this.schedulingApiService.getSessions(
        selectedSiteId,
        startDate,
        endDate,
        this.examWithTA, eligibilityId, this.selectedExamDetailService.timeFormat).subscribe(sessions => {
          sessions.forEach((session: Session) => {
            let index = this.sessionGroups.findIndex(sg => sg.groupId === session.groupId);
            if (index < 0) {
              const groupSessions = sessions.filter(s => s.groupId === session.groupId);
              this.sessionGroups.push(new SessionGroup(session.groupId, groupSessions));
            }
          });
          this.selectedExamDetailService.setSessionGroups(this.sessionGroups);
          observer.next(this.sessionGroups);
          observer.complete();
        });
    });
  }

  updateSessionAvailabilities() {
    this.sessionAvailabilities = [];
    this.exam.sessions.forEach((s, index) => {
      let availability = this.sessionGroups.map(sg => {
        s.duration = sg.sessions.filter(group => group.sessionId === index + 1)[0].slot.duration;
        return {
          "sessionId": index + 1,
          "groupId": sg.sessions.filter(group => group.sessionId === index + 1)[0].groupId,
          "slot": sg.sessions.filter(group => group.sessionId === index + 1)[0].slot,
          "testDate": sg.sessions.filter(group => group.sessionId === index + 1)[0].slot.testDate,
          "startDateTime": sg.sessions.filter(group => group.sessionId === index + 1)[0].slot.startDateTime,
          "duration": sg.sessions.filter(group => group.sessionId === index + 1)[0].slot.duration
        };
      });

      let validGroups: any[] = null;
      if (index > 0 && !!this.sessions && !!this.sessions[index - 1] && !!this.sessions[index - 1].selectedDate) {
        validGroups = this.sessionGroups.map(sg => {
          let prevSessionAvail = sg.sessions.filter(group => group.sessionId === index)[0];
          if (moment(prevSessionAvail.slot.testDate).format('YYYY-MM-DD') === moment(this.sessions[index - 1].selectedDate).format('YYYY-MM-DD')) {
            return prevSessionAvail.groupId;
          }
        });
      }

      availability = availability.reduce((a: any, c: any) => {
        let isValidAvailability = !!validGroups ? validGroups.indexOf(c.groupId) !== -1 : true;
        const x = a.find((item: any) => item.testDate === c.testDate);
        if (!x && isValidAvailability) {
          return a.concat([c]);
        } else {
          return a;
        }
      }, []);
      this.sessionAvailabilities.push(availability);
    });
    this.selectedExamDetailService.setSessionAvailabilities(this.sessionAvailabilities);
  }

  /* Get the location selected by user and emit to parent */
  getGeoLocation(event: any, value: any): void {
    this.searchRegion = { id: '', lat: this.selectedLocation[0], long: this.selectedLocation[1], uri: '', name: this.selectedLocation[2] };
    this.formattedAddress = this.searchRegion.name;
  }

  /* Refresh the sites page if user changes region from pop up */
  refreshSitesByRegion(event: any): void {
    this.region$ = this.selectedExamDetailService.region.subscribe(region => {
      this.region = region;
    });
    this.popLocationSelector.actionTxt = this.region.name;
    this.lat = this.region.lat;
    this.lng = this.region.long;
    this.zoom = this.zoomDefault;
    var selectedStartDate = this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedStartDate['jsdate']), "-");
    var selectedEndDate = this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedEndDate['jsdate']), "-");
    this.skipSiteSlotsize = 0;
    this.selectedExamDetailService.setSkipSize(this.skipSiteSlotsize);
    this.sites = [];
    this.selectedExamDetailService.setSiteSlots([]);
    this.getSites(selectedStartDate, selectedEndDate);
    // sets location for /scheduling/scheduleMultiday
    this.selectedAppointments[this.selectedExamDetailService.selectedAppointmentIndex].region = this.region;
    // sets location for /scheduling/scheduleLocation
    this.locationSelector.region = this.region;
    this.clearAll();
  }

    submitSearch() {
    this.selectedExamDetailService.setRegion(this.searchRegion);

    if (this.region) {
      this.lat = this.region.lat;
      this.lng = this.region.long;
    }

    var formatStartDate = this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedStartDate['jsdate']), "-");
    var formatEndDate = this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedEndDate['jsdate']), "-");
    this.skipSiteSlotsize = 0;
    this.selectedExamDetailService.setSkipSize(this.skipSiteSlotsize);
    this.zoom = this.zoomDefault;

    this.getSites(formatStartDate, formatEndDate);

    // sets location for /scheduling/scheduleMultiday
    this.selectedAppointments[this.selectedExamDetailService.selectedAppointmentIndex].region = this.region;
    // sets location for /scheduling/scheduleLocation
    this.locationSelector.region = this.region;
    this.clearAll();
  }

  canSearch() {
    return !!this.selectedStartDate
      && !!this.selectedEndDate
      && (!this.isMapsValid || !!this.searchRegion)
      && (!!this.region || !!this.country);
  }

  clearSlotSelection() {
    this.selectedSlots = [];
    if (this.holds && this.holds.length) {
      this.holds = [];
      this.selectedExamDetailService.setHolds([]);
    }
    this.validateNavigation();
    this.timerService.clearClock();
  }

  clearSessions() {
    this.sessions = [];
    let session: Session = new Session(
      this.overLapStartDate,
      this.overLapEndDate
    );

    this.sessions.push(session);
    this.exam.sessions.forEach((s) => {
      s.slot = null;
      s.site = null;
    });
    this.chosenSessions.emit(this.exam.sessions);
  }

  clearHolds() {
    if (this.holds && this.holds.length) {
      const releaseHoldTasks$: Observable<any>[] = [];

      this.holds.forEach((hold: any) => {
        // Exam Set Call to check b2b for each exams for eligibility
        releaseHoldTasks$.push(this.schedulingApiService.releaseHold(hold));
      });

      forkJoin(releaseHoldTasks$).subscribe(resp => {
        this.holds = [];
        this.selectedExamDetailService.setHolds([]);
      });
    }
  }

  distinct(value: any, index: number, self: any) {
    return self.indexOf(value) === index;
  }

  /**
   * Helper method to add duration
   * @param date
   * @param duration
   */
  getEndDateTime(date: string, duration: any) {
    //duration = this.getDuration(duration);
    return moment(date)
      .add(duration, 'minutes')
      .format('YYYY-MM-DDTHH:mm:ss');
  }

  getValidEndDateStart(beginDate: any) {
    if (!beginDate) return;
    return this.utilityService.getRangeEndDateStart(beginDate);
  }

  getValidEndDateEnd(beginDate: any) {
    if (!beginDate) return;
    const maxDays = this.selectedExamDetailService.maxDays > 0 ? this.selectedExamDetailService.maxDays : 14;
    return this.utilityService.getRangeEndDateEnd(beginDate, maxDays, this.overLapEndDate);
  }

  /**
 * Helper method to gtet common elements from Arrays
 * @param arrays
 */
  getCommonElements(arrays: number[][]) {
    let currentValues = {};
    let commonValues = {};
    for (let i = arrays[0].length - 1; i >= 0; i--) {
      currentValues[arrays[0][i]] = 1;
    }
    for (let i = arrays.length - 1; i > 0; i--) {
      let currentArray = arrays[i];
      for (let j = currentArray.length - 1; j >= 0; j--) {
        if (currentArray[j] in currentValues) {
          commonValues[currentArray[j]] = 1;
        }
      }
      currentValues = commonValues;
      commonValues = {};
    }
    return Object.keys(currentValues).map(function (value) {
      return parseInt(value);
    });
  }

  /**
   * Helper method to get distinct values from Arrays
   * @param array
   */
  getDistinctValues(array: any[]) {
    return array.reduce((a: any, c: any) => {
      const x = a.find((item: any) => item.date === c.date);
      if (!x) {
        return a.concat([c]);
      } else {
        return a;
      }
    }, []);
  }

  getFocus(event: any): void {
    if (event) {
      setTimeout(function () {
        event.popover.elementRef.nativeElement.nextElementSibling.focus();
      }, 100);
    }
  }


  activeClass(avldate: string, i: number, classString: string): string {
    return this.isDateSelected(avldate, i) ? classString : '';
  }

  isDateSelected(avldate: string, i: number): boolean {
    return !!this.selectedSite && this.sites[i].id == this.selectedSite.id && avldate == this.selectedSite.Testdate;
  }

  activeCardClass(avlDate: any, i: number, classString: string): string {
    return this.isCardDateSelected(avlDate, i) ? classString : '';
  }

  isCardDateSelected(avlDate: any, i: number): boolean {
    return !!this.sessions[i] && this.sessions[i].selectedDate === avlDate.date;
  }

  setFormControl(avldate: string) {
    return this.utilityService.convertDateToWords(avldate, ',');
  }

  convertIndexToSession(i: number) {
    return i + 1;
  }
}
